import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { FaRoute } from "react-icons/fa";
import { Spinner as BsSpinner } from "react-bootstrap";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Filter,
  Inject,
} from "@syncfusion/ej2-react-grids";

import { useTheme } from "../../contexts/ThemeContext";
import { routingService } from "../../Services/routing.service";
import Spinner from "../Components/Spinner";
import {
  parseOriginDestinationFromMapLink,
  parseLatLngString,
} from "./utils/googleMaps";

export default function RoutesList() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  // Cache de geocodificação reversa: chave = "lat,lng", valor = endereço (string) | null (em andamento) | false (falhou)
  const [geocodedLabels, setGeocodedLabels] = useState({});

  useEffect(() => {
    dispatch({ type: "title", value: t("ROUTING.TITLE") });
  }, [dispatch, t]);

  useEffect(() => {
    loadRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRoutes = () => {
    setLoading(true);
    routingService
      .fetchAllRoutes()
      .then((res) => setRoutes(Array.isArray(res) ? res : []))
      .finally(() => setLoading(false));
  };

  const goToRoutingScreen = () => navigate("/routing/new");

  const buildSanitizedRoute = (routeData) => ({
    routeId: routeData.routeId ?? routeData.RouteId ?? routeData.id,
    startLocation: routeData.startLocation ?? routeData.StartLocation ?? null,
    endLocation: routeData.endLocation ?? routeData.EndLocation ?? null,
    originName: routeData.originName ?? null,
    destinationName: routeData.destinationName ?? null,
    mapLink: routeData.mapLink ?? routeData.MapLink ?? null,
    instructions: routeData.instructions ?? routeData.Instructions ?? null,
    vehicleType: routeData.vehicleType ?? routeData.VehicleType ?? null,
    optimized: routeData.optimized ?? routeData.Optimized ?? null,
    intermediatePoints:
      routeData.intermediatePoints ?? routeData.IntermediatePoints ?? null,
  });

  const openRouteInRoutingScreen = (routeData) => {
    navigate("/routing/new", { state: { route: buildSanitizedRoute(routeData) } });
  };


  const openMap = (routeData) => {
    navigate("/routing/new", {
      state: { route: buildSanitizedRoute(routeData), autoOpenMap: true },
    });
  };

  // Alguns registros vêm do backend com startLocation/endLocation vazios.
  // Nesse caso, extraímos origem/destino das coordenadas presentes no mapLink.
  const gridData = useMemo(
    () =>
      routes.map((r) => {
        const mapLink = r.mapLink ?? r.MapLink;
        let originName = r.startLocation ?? r.StartLocation ?? r.originName ?? r.OriginName;
        let destinationName = r.endLocation ?? r.EndLocation ?? r.destinationName ?? r.DestinationName;

        if (!originName || !destinationName) {
          const { origin, destination } = parseOriginDestinationFromMapLink(mapLink);
          if (!originName) originName = origin;
          if (!destinationName) destinationName = destination;
        }

        return {
          ...r,
          routeId: r.routeId ?? r.RouteId ?? r.id,
          originName: originName || null,
          destinationName: destinationName || null,
          mapLink,
          vehicleType: r.vehicleType ?? r.VehicleType,
        };
      }),
    [routes]
  );

  // Geocodifica (reverso) as origens/destinos que caíram no fallback de coordenada.
  useEffect(() => {
    const geocoder =
      typeof window !== "undefined" && window.google?.maps?.Geocoder
        ? new window.google.maps.Geocoder()
        : null;
    if (!geocoder) return;

    const coordsToResolve = new Set();
    gridData.forEach((r) => {
      [r.originName, r.destinationName].forEach((val) => {
        if (val && parseLatLngString(val) && geocodedLabels[val] === undefined) {
          coordsToResolve.add(val);
        }
      });
    });

    if (coordsToResolve.size === 0) return;

    coordsToResolve.forEach((coordStr) => {
      setGeocodedLabels((prev) => ({ ...prev, [coordStr]: null })); // marca como "em andamento"
      const { lat, lng } = parseLatLngString(coordStr);
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results?.[0]?.formatted_address) {
          setGeocodedLabels((prev) => ({ ...prev, [coordStr]: results[0].formatted_address }));
        } else {
          setGeocodedLabels((prev) => ({ ...prev, [coordStr]: false }));
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridData]);

  const renderLocation = (value) => {
    if (!value) return <span className="text-secondary">—</span>;

    const isCoord = !!parseLatLngString(value);
    if (!isCoord) return <span>{value}</span>;

    const resolved = geocodedLabels[value];
    if (resolved === undefined || resolved === null) {
      return (
        <span className="d-flex align-items-center gap-1 text-secondary">
          <BsSpinner animation="border" size="sm" /> {value}
        </span>
      );
    }
    if (resolved === false) return <span>{value}</span>; // geocodificação falhou, mantém coordenada
    return <span>{resolved}</span>;
  };

  const actionTemplate = (props) => (
    <div className="d-flex gap-2">
      <button
        className="btn btn-sm btn-outline-primary"
        style={isDark ? { color: "#fff", borderColor: "#fff" } : {}}
        onClick={() => openRouteInRoutingScreen(props)}
      >
        Ver detalhes
      </button>
      <button
        className="btn btn-sm btn-outline-secondary"
        style={isDark ? { color: "#fff", borderColor: "#fff" } : {}}
        onClick={() => openMap(props)}
        title="Recalcula a rota e abre o mapa"
      >
        Ver Mapa
      </button>
    </div>
  );

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Rotas salvas</h5>
        <button
          type="button"
          className="btn btn-primary d-flex align-items-center gap-2"
          onClick={goToRoutingScreen}
          style={{ fontSize: 10 }}
        >
          <FaRoute size={13} /> Roteirizar
        </button>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center my-5">
          <Spinner />
        </div>
      ) : routes.length === 0 ? (
        <div className="text-center text-secondary my-5" style={{ fontSize: 10 }}>
          Nenhuma rota salva ainda.
        </div>
      ) : (
        <div className="card grid-card" style={{ marginTop: 8 }}>
          <GridComponent
            dataSource={gridData}
            allowFiltering={true}
            filterSettings={{ type: "Menu" }}
            rowHeight={30}
            height="auto"
            locale="pt-BR"
          >
            <ColumnsDirective>
              <ColumnDirective
                field="originName"
                headerText="Origem"
                width="200"
                template={(props) => renderLocation(props.originName)}
              />
              <ColumnDirective
                field="destinationName"
                headerText="Destino"
                width="200"
                template={(props) => renderLocation(props.destinationName)}
              />
              <ColumnDirective headerText="" width="280" template={actionTemplate} />
            </ColumnsDirective>
            <Inject services={[Filter]} />
          </GridComponent>
        </div>
      )}
    </div>
  );
}
